<script setup>
import { computed, useAttrs } from 'vue'

/**
 * Branded `<select>` mirror of `<TextInput>`. Encodes the same focus-ring,
 * border, surface, and padding so dropdowns line up visually with text fields
 * in the same form.
 *
 * Same aria-invalid + aria-describedby wiring as TextInput:
 *   - `aria-invalid="true"` when `:error` is set
 *   - `aria-describedby` derived from the input's own `id` by appending `-error`
 *
 * Callers pair this with `<InputError :id="`${selectId}-error`" :message />`
 * so the screen reader announces the validation error when focus lands here.
 *
 * Options come via the default slot - callers own the `<option>` markup
 * (including dynamic `v-for` and the disabled placeholder if needed).
 */
defineProps({
    modelValue: {
        type: [String, Number],
        default: '',
    },
    error: {
        type: String,
        default: null,
    },
    placeholder: {
        type: String,
        default: null,
    },
    /**
     * Visual sizing. `default` (p-4, matches TextInput) is the form recipe;
     * `compact` (px-3 py-2 text-sm) is for toolbar filter chips that need to
     * sit alongside other small controls in a horizontal bar.
     */
    size: {
        type: String,
        default: 'default',
        validator: (v) => ['default', 'compact'].includes(v),
    },
})

const emit = defineEmits(['update:modelValue', 'change'])

function onChange(event) {
    emit('update:modelValue', event.target.value)
    emit('change', event)
}

const attrs = useAttrs()

// Derive the error-message id from the select's own id so the
// aria-describedby link is automatic (matches TextInput).
const errorId = computed(() => (attrs.id ? `${attrs.id}-error` : undefined))
</script>

<template>
    <!-- Visual recipe mirrors TextInput so dropdowns sit in line with text
         fields in the same form. Bumped from -low to -high surface tone for
         clear contrast against the page background. -->
    <select
        class="bg-surface-container-high text-on-surface focus:border-primary-container focus:ring-primary-container/70 w-full rounded-md border transition-all focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        :class="[size === 'compact' ? 'px-3 py-2 text-sm' : 'p-4', error ? 'border-error' : 'border-zinc-700']"
        :value="modelValue"
        :aria-invalid="error ? 'true' : undefined"
        :aria-describedby="error ? errorId : undefined"
        @change="onChange"
    >
        <option
            v-if="placeholder"
            value=""
            disabled
        >
            {{ placeholder }}
        </option>
        <slot />
    </select>
</template>
